/**
 * ChatbotWidget — assistente global contextual flutuante (fase Chatbot).
 *
 * Substitui o static/js/global_chatbot.js nas telas da SPA: botão
 * flutuante, painel com histórico por contexto, sugestões rápidas e
 * limpeza. O contexto vem da ROTA da SPA (a réplica da detecção por URL
 * do widget legado) — a conversa do ETP não se mistura com a do TR.
 */

import {
  faBroom,
  faPaperPlane,
  faRobot,
  faSpinner,
  faTimes,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router'
import {
  historicoChatbot,
  limparHistoricoChatbot,
  perguntarChatbot,
  sugestoesChatbot,
  type MensagemChatbot,
} from '../api/chatbot'

const NOME_CONTEXTO: Record<string, string> = {
  dashboard: 'Manual de Ajuda',
  etp: 'ETP',
  dfd: 'DFD',
  tr: 'Termo de Referência',
  audit: 'Auditoria',
  legal_research: 'Pesquisa Jurídica',
  sus: 'Pareceres SUS',
  juridico: 'Jurídico',
  general: 'Assistente',
}

/** Réplica do detectContext() do widget legado, sobre as rotas da SPA. */
function contextoDaRota(caminho: string): string {
  const c = caminho.toLowerCase()
  if (c === '/' || c.startsWith('/dashboard')) return 'dashboard'
  if (c.startsWith('/etp')) return 'etp'
  if (c.startsWith('/dfd')) return 'dfd'
  if (c.startsWith('/tr')) return 'tr'
  if (c.startsWith('/sus')) return 'sus'
  if (c.startsWith('/auditoria')) return 'audit'
  if (c.startsWith('/jurisprudencia')) return 'legal_research'
  if (c.startsWith('/juridico')) return 'juridico'
  return 'general'
}

function Balao({ mensagem }: { mensagem: MensagemChatbot }) {
  const doUsuario = mensagem.papel === 'user'
  return (
    <div className={`flex ${doUsuario ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
          doUsuario
            ? 'rounded-br-md bg-gradient-to-r from-indigo-600 to-purple-600 text-white'
            : 'rounded-bl-md bg-slate-100 text-slate-800 dark:bg-slate-700 dark:text-slate-100'
        }`}
      >
        {mensagem.conteudo}
      </div>
    </div>
  )
}

export function ChatbotWidget() {
  const localizacao = useLocation()
  const contexto = contextoDaRota(localizacao.pathname)
  const [aberto, setAberto] = useState(false)
  const [texto, setTexto] = useState('')
  const clienteQuery = useQueryClient()
  const fimRef = useRef<HTMLDivElement>(null)

  const { data: historico } = useQuery({
    queryKey: ['chatbot-historico', contexto],
    queryFn: () => historicoChatbot(contexto),
    enabled: aberto,
    retry: false,
  })
  const { data: sugestoes } = useQuery({
    queryKey: ['chatbot-sugestoes', contexto],
    queryFn: () => sugestoesChatbot(contexto),
    enabled: aberto,
    staleTime: Infinity,
    retry: false,
  })

  const perguntar = useMutation({
    mutationFn: (mensagem: string) =>
      perguntarChatbot(mensagem, contexto, {
        pageTitle: document.title.slice(0, 200),
      }),
    onSettled: () => {
      void clienteQuery.invalidateQueries({
        queryKey: ['chatbot-historico', contexto],
      })
    },
  })

  const limpar = useMutation({
    mutationFn: () => limparHistoricoChatbot(contexto),
    onSuccess: () => {
      void clienteQuery.invalidateQueries({
        queryKey: ['chatbot-historico', contexto],
      })
    },
  })

  const mensagens = historico?.mensagens ?? []
  // pergunta em trânsito aparece imediatamente, antes do refetch
  const pendente = perguntar.isPending ? perguntar.variables : null

  useEffect(() => {
    fimRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [mensagens.length, pendente, aberto])

  const enviar = (mensagem: string) => {
    const limpa = mensagem.trim()
    if (!limpa || perguntar.isPending) return
    setTexto('')
    perguntar.mutate(limpa)
  }

  if (!aberto) {
    return (
      <button
        type="button"
        onClick={() => setAberto(true)}
        aria-label="Abrir assistente LicitIA"
        className="fixed bottom-6 right-6 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-indigo-600 to-purple-600 text-xl text-white shadow-xl shadow-indigo-500/40 transition-transform hover:scale-110"
      >
        <FontAwesomeIcon icon={faRobot} />
      </button>
    )
  }

  return (
    <div
      className="fixed bottom-6 right-6 z-40 flex h-[32rem] w-[22rem] max-w-[calc(100vw-3rem)] flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl dark:border-slate-700 dark:bg-dark-card"
      role="dialog"
      aria-label="Assistente LicitIA"
    >
      <header className="flex items-center justify-between bg-gradient-to-r from-indigo-600 to-purple-600 px-4 py-3 text-white">
        <div className="flex items-center gap-2">
          <FontAwesomeIcon icon={faRobot} />
          <div>
            <div className="text-sm font-bold">Assistente LicitIA</div>
            <div className="text-xs text-indigo-200">
              {NOME_CONTEXTO[contexto] ?? 'Assistente'}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => limpar.mutate()}
            aria-label="Limpar conversa"
            title="Limpar conversa"
            className="rounded-lg p-2 transition-colors hover:bg-white/20"
          >
            <FontAwesomeIcon icon={faBroom} className="text-sm" />
          </button>
          <button
            type="button"
            onClick={() => setAberto(false)}
            aria-label="Fechar assistente"
            className="rounded-lg p-2 transition-colors hover:bg-white/20"
          >
            <FontAwesomeIcon icon={faTimes} className="text-sm" />
          </button>
        </div>
      </header>

      <div className="flex-1 space-y-3 overflow-y-auto p-4">
        {mensagens.length === 0 && !pendente && (
          <div className="mt-4 text-center text-sm text-slate-500">
            <FontAwesomeIcon icon={faRobot} className="mb-2 text-3xl text-indigo-300" />
            <p>
              Olá! Sou o assistente da LicitIA. Pergunte sobre{' '}
              {NOME_CONTEXTO[contexto] ?? 'licitações'} ou sobre a Lei
              14.133/2021.
            </p>
          </div>
        )}
        {mensagens.map((m, i) => (
          <Balao key={`${m.quando}-${i}`} mensagem={m} />
        ))}
        {pendente && (
          <>
            <Balao mensagem={{ papel: 'user', conteudo: pendente, quando: '' }} />
            <div className="flex justify-start">
              <div className="rounded-2xl rounded-bl-md bg-slate-100 px-4 py-2.5 text-sm text-slate-500 dark:bg-slate-700 dark:text-slate-400">
                <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
                Pensando…
              </div>
            </div>
          </>
        )}
        {perguntar.isError && (
          <p className="text-center text-xs text-red-600 dark:text-red-400">
            {(perguntar.error as Error).message}
          </p>
        )}
        <div ref={fimRef} />
      </div>

      {mensagens.length === 0 && !pendente && (sugestoes?.sugestoes.length ?? 0) > 0 && (
        <div className="flex flex-wrap gap-2 border-t border-slate-100 px-4 py-2 dark:border-slate-700">
          {sugestoes!.sugestoes.slice(0, 3).map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => enviar(s)}
              className="rounded-full border border-indigo-200 bg-indigo-50 px-3 py-1 text-xs text-indigo-700 transition-colors hover:bg-indigo-100 dark:border-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300"
            >
              {s}
            </button>
          ))}
        </div>
      )}

      <form
        className="flex items-center gap-2 border-t border-slate-200 p-3 dark:border-slate-700"
        onSubmit={(e) => {
          e.preventDefault()
          enviar(texto)
        }}
      >
        <label htmlFor="chatbot-mensagem" className="sr-only">
          Sua pergunta
        </label>
        <input
          id="chatbot-mensagem"
          type="text"
          value={texto}
          maxLength={2000}
          placeholder="Digite sua pergunta…"
          onChange={(e) => setTexto(e.target.value)}
          className="flex-1 rounded-xl border border-slate-300 px-3 py-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-slate-600 dark:bg-dark-bg dark:text-slate-100"
        />
        <button
          type="submit"
          disabled={perguntar.isPending || !texto.trim()}
          aria-label="Enviar pergunta"
          className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 text-white transition-all hover:from-indigo-700 hover:to-purple-700 disabled:opacity-50"
        >
          <FontAwesomeIcon icon={perguntar.isPending ? faSpinner : faPaperPlane} spin={perguntar.isPending} />
        </button>
      </form>
    </div>
  )
}
